import api from './api';

export const shippingService = {
  // ============ PRZESYŁKI ============

  /**
   * Pobierz listę przesyłek z filtrami
   * @param {Object} params - Parametry: status, date_from, date_to, customer_id, search, page, per_page
   */
  async getShipments(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.status) queryParams.append('status', params.status);
      if (params.date_from) queryParams.append('date_from', params.date_from);
      if (params.date_to) queryParams.append('date_to', params.date_to);
      if (params.customer_id) queryParams.append('customer_id', params.customer_id);
      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page);
      if (params.per_page) queryParams.append('per_page', params.per_page);

      const response = await api.get(`shipping/shipments?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania przesyłek:', error);
      throw new Error(error.message || 'Błąd pobierania przesyłek');
    }
  },

  /**
   * Utwórz nową przesyłkę
   * @param {Object} data - Dane przesyłki
   */
  async createShipment(data) {
    try {
      const response = await api.post('shipping/shipments', data);
      return response.data;
    } catch (error) {
      console.error('Błąd tworzenia przesyłki:', error);
      throw new Error(error.message || 'Błąd tworzenia przesyłki');
    }
  },

  /**
   * Pobierz status przesyłki
   * @param {string} shipmentId - ID przesyłki InPost
   */
  async getShipmentStatus(shipmentId) {
    try {
      const response = await api.get(`shipping/shipments/${shipmentId}/status`);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania statusu:', error);
      throw new Error(error.message || 'Błąd pobierania statusu');
    }
  },

  /**
   * Anuluj przesyłkę
   * @param {string} shipmentId - ID przesyłki InPost
   */
  async cancelShipment(shipmentId) {
    try {
      const response = await api.post(`shipping/shipments/${shipmentId}/cancel`);
      return response.data;
    } catch (error) {
      console.error('Błąd anulowania przesyłki:', error);
      throw new Error(error.message || 'Błąd anulowania przesyłki');
    }
  },

  /**
   * Pobierz etykietę przesyłki (PDF)
   * @param {string} shipmentId - ID przesyłki InPost
   */
  async downloadLabel(shipmentId) {
    try {
      const response = await api.get(`shipping/shipments/${shipmentId}/label`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania etykiety:', error);
      throw new Error(error.message || 'Błąd pobierania etykiety');
    }
  },

  /**
   * Kup/opłać przesyłkę
   * @param {string} shipmentId - ID przesyłki InPost
   */
  async buyShipment(shipmentId) {
    try {
      const response = await api.post(`shipping/shipments/${shipmentId}/buy`);
      return response.data;
    } catch (error) {
      console.error('Błąd opłacania przesyłki:', error);
      throw new Error(error.message || 'Błąd opłacania przesyłki');
    }
  },

  // ============ ŚLEDZENIE ============

  /**
   * Śledź przesyłkę po numerze
   * @param {string} trackingNumber - Numer śledzenia
   */
  async trackShipment(trackingNumber) {
    try {
      const response = await api.get(`shipping/track/${trackingNumber}`);
      return response.data;
    } catch (error) {
      console.error('Błąd śledzenia przesyłki:', error);
      throw new Error(error.message || 'Błąd śledzenia przesyłki');
    }
  },

  // ============ PACZKOMATY ============

  /**
   * Pobierz listę paczkomatów/punktów InPost
   * @param {Object} params - Parametry: city, post_code, name, type
   */
  async getPoints(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.city) queryParams.append('city', params.city);
      if (params.post_code) queryParams.append('post_code', params.post_code);
      if (params.name) queryParams.append('name', params.name);
      if (params.type) queryParams.append('type', params.type);
      if (params.per_page) queryParams.append('per_page', params.per_page);

      const config = params.signal ? { signal: params.signal } : undefined;
      const response = await api.get(`shipping/points?${queryParams}`, config);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania paczkomatów:', error);
      throw new Error(error.message || 'Błąd pobierania paczkomatów');
    }
  },

  /**
   * Pobierz szczegóły paczkomatu
   * @param {string} pointName - Nazwa paczkomatu (np. WAW01A)
   */
  async getPoint(pointName) {
    try {
      const response = await api.get(`shipping/points/${pointName}`);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania paczkomatu:', error);
      throw new Error(error.message || 'Błąd pobierania paczkomatu');
    }
  },

  // ============ USŁUGI I SZABLONY ============

  /**
   * Pobierz dostępne usługi wysyłkowe
   */
  async getServices() {
    try {
      const response = await api.get('shipping/services');
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania usług:', error);
      throw new Error(error.message || 'Błąd pobierania usług');
    }
  },

  /**
   * Pobierz szablony rozmiarów paczek
   */
  async getParcelTemplates() {
    try {
      const response = await api.get('shipping/parcel-templates');
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania szablonów paczek:', error);
      throw new Error(error.message || 'Błąd pobierania szablonów paczek');
    }
  },

  // ============ STATYSTYKI ============

  /**
   * Pobierz statystyki przesyłek
   */
  async getStats() {
    try {
      const response = await api.get('shipping/stats');
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania statystyk:', error);
      throw new Error(error.message || 'Błąd pobierania statystyk');
    }
  },

  // ============ USTAWIENIA ============

  /**
   * Pobierz ustawienia wysyłki
   */
  async getSettings() {
    try {
      const response = await api.get('shipping/settings');
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania ustawień:', error);
      throw new Error(error.message || 'Błąd pobierania ustawień');
    }
  },

  /**
   * Test połączenia z API InPost
   */
  async testConnection() {
    try {
      const response = await api.post('shipping/test-connection');
      return response.data;
    } catch (error) {
      console.error('Błąd testu połączenia:', error);
      throw new Error(error.message || 'Błąd testu połączenia');
    }
  }
};

export default shippingService;
